import { createHyperLinkToolParamsSchema } from "@redotech/redo-model/agents/platform-agents/settings/create-hyper-link";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { ToolComponentProps } from "@redotech/redo-web/ai/chat/types/tool-component-props";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

export function CreateHyperLinkTool(props: ToolComponentProps) {
  const { sendToolResponse } = useContext(AiChatContext);

  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  const hyperLink = useMemo(() => {
    if (props.input) {
      const result = createHyperLinkToolParamsSchema.safeParse(props.input);
      if (result.success) {
        return result.data;
      }
    }
    return undefined;
  }, [props.input]);

  if (cancelled) {
    return <Text>User cancelled navigation.</Text>;
  }

  if (submitted) {
    return <Text>User navigated to the setting.</Text>;
  }

  if (!hyperLink) {
    return (
      <Text color="error">
        AI generated a bad navigation link. Sorry about that.
      </Text>
    );
  }
  return (
    <Flex bgColor="primary" dir="column" gap="md" p="2xl">
      <div style={{ whiteSpace: "pre-wrap" }}>
        {hyperLink.messageToMerchant.split("\n").map((line, i, arr) => {
          // Handle bullet points
          if (line.trim().startsWith("- ")) {
            return (
              <div key={i} style={{ marginLeft: "1rem" }}>
                • {line.trim().slice(2)}
              </div>
            );
          }
          // Regular text with line breaks between paragraphs
          return (
            <div key={i}>
              {line}
              {i < arr.length - 1 && line.trim() === "" && <br />}
            </div>
          );
        })}
      </div>

      <Flex align="flex-start" dir="row" justify="space-evenly">
        <RedoButton
          hierarchy="secondary"
          onClick={() => {
            setCancelled(true);
            void sendToolResponse({
              toolCallId: props.toolCallId,
              toolName: props.toolName,
              result: { output: "User cancelled the navigation" },
            });
          }}
          size="sm"
          text="Cancel"
        />
        <RedoButton
          hierarchy="primary"
          onClick={() => {
            setSubmitted(true);

            const [link, parameters] = hyperLink.hyperLink.split("?");
            const url = {
              pathname: `/${link}`,
              search: `?highlightText=${encodeURIComponent(hyperLink.regexToMatch)}${parameters ? `&${parameters}` : ""}`,
            };
            navigate(url, { replace: true });
            void sendToolResponse({
              toolCallId: props.toolCallId,
              toolName: props.toolName,
              result: { output: "User navigated to the setting" },
            });
          }}
          text="Go to setting"
        />
      </Flex>
    </Flex>
  );
}
